import React, { useState, useEffect } from 'react';
import { UserSquare, Search, Save, Pencil, MessageCircle, Plus } from 'lucide-react';
import api from '../services/api';
import { showToast } from '../utils/toast';
import '../styles/AdminComponents.css';
import '../styles/ContactsManagement.css';

function ContactsManagement({ onStartConversation }) {
  const [starting, setStarting] = useState(null);

  const startChat = async (c) => {
    if (starting) return;
    setStarting(c.id);
    try {
      const res = await api.post('/conversations', {
        contactNumber: c.phone_number,
        contactName: c.name || c.phone_number
      });
      const convId = res.data.id;
      // Busca a conversa completa para passar ao Dashboard
      const conv = await api.get(`/conversations/${convId}`);
      if (onStartConversation) onStartConversation(conv.data);
      else showToast('Conversa criada (id ' + convId + ')', 'success');
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao abrir conversa', 'error');
    } finally {
      setStarting(null);
    }
  };

  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', notes: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone_number: '', email: '', company: '', notes: '' });
  const [adding, setAdding] = useState(false);

  const submitAdd = async () => {
    if (!addForm.phone_number.trim()) {
      showToast('Número é obrigatório', 'warning');
      return;
    }
    setAdding(true);
    try {
      await api.post('/contacts', addForm);
      showToast('Contato adicionado', 'success');
      setShowAdd(false);
      setAddForm({ name: '', phone_number: '', email: '', company: '', notes: '' });
      load();
    } catch (e) {
      showToast(e.response?.data?.message || 'Erro ao adicionar contato', 'error');
    } finally {
      setAdding(false);
    }
  };

  const load = async () => {
    try {
      const res = await api.get('/contacts', { params: search ? { search } : {} });
      setContacts(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [search]);

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name || '', email: c.email || '', company: c.company || '', notes: c.notes || '' });
  };

  const save = async () => {
    try {
      await api.put(`/contacts/${editing}`, form);
      setEditing(null);
      load();
    } catch (e) { alert('Erro ao salvar'); }
  };

  return (
    <div className="admin-page">
      <div className="contacts-header">
        <h2><UserSquare size={20}/> Agenda de Contatos</h2>
        <div className="contacts-header-actions">
          <button onClick={() => setShowAdd(true)} className="contacts-add-btn">
            <Plus size={15}/> Adicionar contato
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Adicionar contato</h3>
            <label>Nome</label>
            <input
              value={addForm.name}
              onChange={e => setAddForm({ ...addForm, name: e.target.value })}
              autoFocus
            />
            <label>Número (com DDD, ex: 11912345678)</label>
            <input
              value={addForm.phone_number}
              onChange={e => setAddForm({ ...addForm, phone_number: e.target.value })}
              placeholder="11912345678"
              inputMode="tel"
            />
            <label>Email</label>
            <input
              type="email"
              value={addForm.email}
              onChange={e => setAddForm({ ...addForm, email: e.target.value })}
            />
            <label>Empresa</label>
            <input
              value={addForm.company}
              onChange={e => setAddForm({ ...addForm, company: e.target.value })}
            />
            <label>Observações</label>
            <textarea
              value={addForm.notes}
              onChange={e => setAddForm({ ...addForm, notes: e.target.value })}
            />
            <div className="modal-actions">
              <button onClick={() => setShowAdd(false)}>Cancelar</button>
              <button onClick={submitAdd} disabled={adding} className="primary">
                <Save size={14}/> {adding ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="search-box">
        <Search size={16}/>
        <input
          placeholder="Buscar por nome, número, email ou empresa..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Desktop: tabela */}
      <div className="contacts-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nome</th><th>Número</th><th>Email</th><th>Empresa</th><th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map(c => (
              <tr key={c.id}>
                <td>{c.name && c.name !== c.phone_number ? c.name : <span style={{color:'#aaa'}}>{c.phone_number}</span>}</td>
                <td>{c.phone_number}</td>
                <td>{c.email}</td>
                <td>{c.company}</td>
                <td className="contacts-actions-cell">
                  <button
                    onClick={() => startChat(c)}
                    disabled={starting === c.id}
                    className="btn-link contacts-chat-btn"
                    title="Iniciar conversa"
                  >
                    <MessageCircle size={14}/> {starting === c.id ? '...' : 'Conversar'}
                  </button>
                  <button onClick={() => startEdit(c)} className="btn-link">Editar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards */}
      <div className="contacts-cards">
        {contacts.map(c => (
          <div key={c.id} className="contact-card">
            <div className="contact-card-name">
              {c.name && c.name !== c.phone_number ? c.name : <span style={{color:'#aaa'}}>{c.phone_number}</span>}
            </div>
            <div className="contact-card-info">{c.phone_number}</div>
            {c.email && <div className="contact-card-info">{c.email}</div>}
            {c.company && <div className="contact-card-info">{c.company}</div>}
            <div className="contact-card-actions">
              <button
                onClick={() => startChat(c)}
                disabled={starting === c.id}
                className="contact-card-chat"
              >
                <MessageCircle size={13}/> {starting === c.id ? '...' : 'Conversar'}
              </button>
              <button onClick={() => startEdit(c)} className="contact-card-edit">
                <Pencil size={13}/> Editar
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Editar contato</h3>
            <label>Nome</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}/>
            <label>Email</label>
            <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}/>
            <label>Empresa</label>
            <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}/>
            <label>Observações / Qualificação</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}/>
            <div className="modal-actions">
              <button onClick={() => setEditing(null)}>Cancelar</button>
              <button onClick={save} className="primary"><Save size={14}/> Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactsManagement;
